import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, X, Smartphone, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { pushSupported, enablePush, disablePush, registerServiceWorker, syncPushIfGranted, getCurrentSubscription } from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = "pdv_push_prompt_dismissed_at";
const DISMISS_DAYS = 7;

/** Soft prompt shown on the first authenticated visit; the real browser prompt only fires on the "Ativar" click. */
export function PushPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    registerServiceWorker();
    if (Notification.permission === "granted") { syncPushIfGranted(); return; }
    if (Notification.permission === "denied") return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_DAYS * 86400000) return;
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, String(Date.now())); setVisible(false); };
  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notificações ativadas! Você será avisado mesmo com o site fechado.");
      setVisible(false);
    } catch (e) {
      toast.error(e?.message || "Não foi possível ativar");
      if (Notification.permission === "denied") setVisible(false);
    }
    setBusy(false);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-24 sm:w-[380px] z-[90] animate-pop-in" data-testid="push-permission-prompt">
      <Card className="p-4 shadow-2xl border-primary/30 bg-background/95 backdrop-blur-md">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-fuchsia-600 text-white flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">Ativar notificações?</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Receba no celular ou computador quando alguém curtir, comentar, compartilhar, mencionar ou marcar você — mesmo com o site fechado.
            </p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={enable} disabled={busy} data-testid="push-prompt-enable">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><BellRing className="w-4 h-4 mr-1" /> Ativar</>}
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss} data-testid="push-prompt-later">Agora não</Button>
            </div>
          </div>
          <button onClick={dismiss} className="text-muted-foreground hover:text-foreground" aria-label="Fechar" data-testid="push-prompt-close"><X className="w-4 h-4" /></button>
        </div>
      </Card>
    </div>
  );
}

/** Settings card: toggle push for this device + test button. */
export function PushSettingsCard() {
  const supported = pushSupported();
  const [enabled, setEnabled] = useState(false);
  const [devices, setDevices] = useState(0);
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState(false);
  const [digestPref, setDigestPref] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/push/status`);
      setDevices(r.data.devices || 0);
      setConfigured(!!r.data.configured);
      const d = await axios.get(`${API}/push/digest/pref`);
      setDigestPref(d.data);
    } catch { /* silent */ }
    if (supported) setEnabled(Notification.permission === "granted" && !!(await getCurrentSubscription()));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const toggleDigest = async (v) => {
    setBusy(true);
    try { await axios.put(`${API}/push/digest/pref`, { enabled: v }); setDigestPref((p) => ({ ...p, enabled: v })); toast.success(v ? "Resumo diário ativado" : "Resumo diário desativado"); }
    catch { toast.error("Erro ao salvar"); }
    setBusy(false);
  };
  const sendDigestNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/push/digest/send-me`);
      const d = r.data.digest;
      if (r.data.sent > 0) toast.success("Resumo enviado para o seu dispositivo!", { description: d?.text });
      else toast.info(d?.text || "Nenhum dispositivo inscrito.", { description: "Ative o push para receber no celular." });
    } catch { toast.error("Erro ao enviar resumo"); }
    setBusy(false);
  };

  const toggle = async (v) => {
    setBusy(true);
    try {
      if (v) { await enablePush(); toast.success("Push ativado neste dispositivo"); }
      else { await disablePush(); toast.success("Push desativado neste dispositivo"); }
    } catch (e) { toast.error(e?.message || "Erro"); }
    await refresh();
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/push/test`);
      if (r.data.sent > 0) toast.success(`Push de teste enviado para ${r.data.sent} dispositivo(s)`);
      else toast.info("Nenhum dispositivo inscrito ainda. Ative o push primeiro.");
    } catch { toast.error("Erro ao enviar teste"); }
    setBusy(false);
  };

  return (
    <Card className="p-5" data-testid="push-settings-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center shrink-0"><Smartphone className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-sm">Push no dispositivo</div>
              <div className="text-xs text-muted-foreground">Curtidas, comentários, compartilhamentos, menções e marcações chegam mesmo com o site fechado.</div>
            </div>
            <Switch checked={enabled} onCheckedChange={toggle} disabled={busy || !supported || !configured || (supported && Notification.permission === "denied")} data-testid="push-toggle" />
          </div>
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs text-muted-foreground">
            {!supported && <span className="text-amber-600" data-testid="push-unsupported">Este navegador não suporta Web Push.</span>}
            {!configured && <span className="text-amber-600">Push não configurado no servidor (VAPID).</span>}
            {supported && Notification.permission === "denied" && <span className="text-amber-600" data-testid="push-denied">Permissão bloqueada — libere nas configurações do navegador.</span>}
            <span data-testid="push-devices">{devices} dispositivo(s) inscrito(s)</span>
            <Button size="sm" variant="outline" onClick={sendTest} disabled={busy || !devices} data-testid="push-test-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Enviar teste
            </Button>
          </div>
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/60 flex-wrap" data-testid="digest-pref-row">
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">Resumo diário (missões + moedas)</div>
              <div className="text-xs text-muted-foreground">Um push por dia às {digestPref ? `${String(((digestPref.hour_utc - 3) + 24) % 24).padStart(2, "0")}:00` : "09:00"} com suas missões do dia e as moedas que você ganhou ontem.</div>
            </div>
            <Switch checked={!!digestPref?.enabled} disabled={busy || !digestPref} onCheckedChange={toggleDigest} data-testid="digest-pref-toggle" />
            <Button size="sm" variant="ghost" onClick={sendDigestNow} disabled={busy || !devices} data-testid="digest-send-me">
              <Send className="w-3.5 h-3.5 mr-1" /> Ver resumo de hoje
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
